import type { ReactNode, ThHTMLAttributes, TdHTMLAttributes } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '../cn.js';
import { Button } from './Button.js';

/**
 * Table primitives for the operational register.
 *
 * Dense, high-contrast, sortable — a data grid's pragmatic default styling re-skinned
 * with the palette, exactly as spec §16 asks. The editorial treatment stays away from
 * here: a 190k-row queue does not want an italic serif.
 *
 * Every table in this product is server-paginated. These components never receive a
 * full result set, which is why there is no client-side sort or filter helper here —
 * providing one would invite someone to use it.
 */
export function TableShell({
  children,
  className,
  footer,
}: {
  children: ReactNode;
  className?: string;
  footer?: ReactNode;
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-[var(--pv-radius-lg)] border border-[var(--pv-line)] bg-[var(--pv-card)]',
        className,
      )}
    >
      <div className="pv-scroll-x">{children}</div>
      {footer && (
        <div className="flex flex-wrap items-center gap-3 border-t border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] px-4 py-2.5 [font-size:var(--pv-text-xs)] text-[var(--pv-muted)]">
          {footer}
        </div>
      )}
    </div>
  );
}

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <table className={cn('w-full border-collapse [font-size:var(--pv-text-sm)]', className)}>
      {children}
    </table>
  );
}

export function Thead({ children }: { children: ReactNode }) {
  return <thead>{children}</thead>;
}

export function Tbody({ children }: { children: ReactNode }) {
  return <tbody>{children}</tbody>;
}

export interface ThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /** Makes the header a sort control and announces the current direction. */
  sortKey?: string;
  activeSort?: { key: string; dir: 'asc' | 'desc' } | null;
  onSort?: (key: string) => void;
  numeric?: boolean;
}

export function Th({
  children,
  className,
  sortKey,
  activeSort,
  onSort,
  numeric,
  ...props
}: ThProps) {
  const isActive = Boolean(sortKey) && activeSort?.key === sortKey;
  const ariaSort: ThHTMLAttributes<HTMLTableCellElement>['aria-sort'] = isActive
    ? activeSort!.dir === 'asc'
      ? 'ascending'
      : 'descending'
    : sortKey
      ? 'none'
      : undefined;

  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={cn(
        'whitespace-nowrap border-b border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] px-3.5 py-2.5',
        'text-left [font-size:var(--pv-text-xs)] font-semibold  text-[var(--pv-muted)]',
        numeric && 'text-right',
        className,
      )}
      {...props}
    >
      {sortKey && onSort ? (
        <button
          type="button"
          onClick={() => onSort(sortKey)}
          className={cn(
            'inline-flex items-center gap-1 transition-colors hover:text-[var(--pv-ink)]',
            isActive && 'text-[var(--pv-ink)]',
            numeric && 'flex-row-reverse',
          )}
        >
          {children}
          {isActive ? (
            activeSort!.dir === 'asc' ? (
              <ArrowUp aria-hidden className="size-3" />
            ) : (
              <ArrowDown aria-hidden className="size-3" />
            )
          ) : (
            <ChevronsUpDown aria-hidden className="size-3 opacity-40" />
          )}
        </button>
      ) : (
        children
      )}
    </th>
  );
}

export interface TdProps extends TdHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
}

export function Td({ children, className, numeric, ...props }: TdProps) {
  return (
    <td
      className={cn(
        'border-b border-[var(--pv-line)] px-3.5 py-2.5 align-top',
        numeric && 'pv-num text-right',
        className,
      )}
      {...props}
    >
      {children}
    </td>
  );
}

export function Tr({
  children,
  onClick,
  selected,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  selected?: boolean;
  className?: string;
}) {
  return (
    <tr
      onClick={onClick}
      // A clickable row must be reachable by keyboard. Without this the whole queue is
      // mouse-only, which fails the accessibility target for the busiest screen we have.
      {...(onClick
        ? {
            tabIndex: 0,
            role: 'button' as const,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            },
          }
        : {})}
      className={cn(
        'last:[&>td]:border-b-0',
        onClick && 'cursor-pointer transition-colors hover:bg-[rgba(195,219,156,0.16)]',
        selected && 'bg-[var(--pv-leaf-soft)]',
        className,
      )}
    >
      {children}
    </tr>
  );
}

/** Secondary line inside a cell — the detail under a primary value. */
export function CellSub({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn('mt-0.5 block [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]', className)}>
      {children}
    </span>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100, 200],
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <>
      <span className="pv-num">
        {total === 0 ? (
          'No results'
        ) : (
          <>
            <strong className="font-semibold text-[var(--pv-ink)]">
              {first.toLocaleString()}–{last.toLocaleString()}
            </strong>{' '}
            of {total.toLocaleString()}
          </>
        )}
      </span>

      {onPageSizeChange && (
        <label className="flex items-center gap-1.5">
          <span className="sr-only">Rows per page</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded-[var(--pv-radius-sm)] border border-[var(--pv-line-strong)] bg-white px-1.5 py-0.5 [font-size:var(--pv-text-xs)]"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n} rows
              </option>
            ))}
          </select>
        </label>
      )}

      <span className="ml-auto flex items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <span className="pv-num px-1">
          Page {page} of {totalPages}
        </span>
        <Button
          size="sm"
          variant="secondary"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </span>
    </>
  );
}
